// frontend/src/utils/errorUtils.js
import { ERROR_MESSAGES } from "../config";

/**
 * Centralized error handling utilities
 * Provides consistent error handling across the application
 */

/**
 * Get a user-friendly error message based on the error object
 * @param {Error} error - The error object
 * @param {string} fallbackMessage - Optional fallback message if no specific message can be determined
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (
  error,
  fallbackMessage = ERROR_MESSAGES.DEFAULT
) => {
  // If the error already has a user message (from API service), use it
  if (error.userMessage) {
    return error.userMessage;
  }

  // If it's an axios error with a response
  if (error.response) {
    // Check for server error message in response
    const serverMessage =
      error.response.data?.message || error.response.data?.error;
    if (serverMessage) {
      return serverMessage;
    }

    // Use status code to determine message
    switch (error.response.status) {
      case 400:
        return ERROR_MESSAGES.VALIDATION_ERROR;
      case 401:
        return ERROR_MESSAGES.UNAUTHORIZED;
      case 403:
        return ERROR_MESSAGES.FORBIDDEN;
      case 404:
        return ERROR_MESSAGES.NOT_FOUND;
      case 422:
        return ERROR_MESSAGES.VALIDATION_ERROR;
      case 500:
        return ERROR_MESSAGES.SERVER_ERROR;
      default:
        return fallbackMessage;
    }
  }

  // Network error (no response)
  if (error.request && !error.response) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  // Use error message if available, otherwise fallback
  return error.message || fallbackMessage;
};

/**
 * Log an error with consistent formatting
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred (e.g., component name, function)
 */
export const logError = (error, context = "App") => {
  const errorMessage = getErrorMessage(error);

  // Error logging removed for production

  // Additional error handling could be added here for production
  // e.g., sending to a logging service

  // Could also send to error tracking service here
  // e.g., Sentry.captureException(error);
};

/**
 * Handle API errors consistently
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 * @param {Function} setError - State setter for error message
 * @param {string} fallbackMessage - Optional fallback message
 */
export const handleApiError = (error, context, setError, fallbackMessage) => {
  // Log the error
  logError(error, context);

  // Set error message for UI
  const errorMessage = getErrorMessage(error, fallbackMessage);
  setError(errorMessage);

  // Return the error message (useful for chaining)
  return errorMessage;
};

/**
 * Format validation errors from the server
 * @param {Object} validationErrors - Object with field names as keys and error messages as values
 * @returns {string} - Formatted error message
 */
export const formatValidationErrors = (validationErrors) => {
  if (!validationErrors || typeof validationErrors !== "object") {
    return ERROR_MESSAGES.VALIDATION_ERROR;
  }

  // Convert validation errors object to list of error messages
  const errorMessages = Object.entries(validationErrors)
    .map(([field, message]) => `${field}: ${message}`)
    .join("\n");

  return errorMessages || ERROR_MESSAGES.VALIDATION_ERROR;
};

export default {
  getErrorMessage,
  logError,
  handleApiError,
  formatValidationErrors,
};
